import { ComponentInternalInstance, computed, getCurrentInstance, reactive, ref, watch } from '@vue/composition-api';
import { SubmitContext, ValueType } from 'tdesign-vue';
import { AllValidateResult } from 'tdesign-vue/es/form/type';
import { i18n_t } from '@/locales/i18n';
import storage, { RangeLimit } from '@/renderer/scripts/utils/storage';
import { StoreKey } from '@/renderer/scripts/utils/storage/StoreKey';
import tips from '@/renderer/scripts/utils/tips';
import storageHandler from '@/renderer/components/dialog/setting/components/components/storageHandler';
import { downloadStreamManager, uploadStreamManager } from '@/renderer/scripts/utils/StreamManager';
import { SPEED_UNIT } from '@/common/constant';
import { useBeforeLeave } from '@/renderer/components/dialog/setting/composition/useBeforeLeave';
import { getSpeedAndUnit } from '@/renderer/scripts/utils/tools/packages/unit';
import uploadTaskManager from '@/renderer/scripts/utils/upload';
import downloadTaskManager from '@/renderer/scripts/utils/download';
import { SystemSetting } from '@/renderer/scripts/utils/StreamManager/utils/SystemSetting';

const SPEED_DEFAULT_VALUE = {
  speedKbs: 128,
  speedMbs: 0.2,
  maxFileLimit: 10,
  downloadMaxFileLimit: 6,
  minFileLimit: 1,
  fileLimit: 5,
};
const defaultFormData: RangeLimit = {
  speedLimit: false,
  speedLimitSize: SPEED_DEFAULT_VALUE.speedKbs,
  batchAuto: true,
  speedLimitUnit: SPEED_UNIT.kbs,
  batchLimit: SPEED_DEFAULT_VALUE.fileLimit,
};
const defaultTransmissionSetting = {
  speedUnits: [SPEED_UNIT.mbs, SPEED_UNIT.kbs].map(value => ({ label: value, value })),
  defaultSpeedUnit: SPEED_UNIT.kbs,
  defaultDownloadSetting: {
    ...defaultFormData,
  },
  defaultUploadSetting: defaultFormData,
};

const validateIntegerField = (value: string) => {
  if (value && !/^\d+$/.test(value)) {
    return {
      result: false,
      message: i18n_t('请输入整数'),
      type: 'error',
    };
  }
  return { result: true };
};
/**
 * 传输设置公用逻辑
 * @param type
 */
export const useTransmissionForm = (type: 'upload' | 'download') => {
  const currComponent = getCurrentInstance() as ComponentInternalInstance;
  const vm = currComponent.proxy;

  const formData = reactive<RangeLimit>({
    speedLimit: false,
    speedLimitSize: 128,
    batchAuto: true,
    speedLimitUnit: SPEED_UNIT.kbs,
    batchLimit: 5,
  });
  // storage的key
  const storeKey = computed<StoreKey>(() => {
    return type === 'upload' ? storage.StoreKey.UPLOAD_TRANSFER_SETTING : storage.StoreKey.DOWNLOAD_TRANSFER_SETTING;
  });
  // 小数点位
  const speedDecimalPlaces = computed<number>(() => {
    let decimalPlaces = 0;
    if (formData.speedLimitUnit === SPEED_UNIT.mbs) {
      decimalPlaces = 1;
    }
    return decimalPlaces;
  });

  const sizeAndUnitValidator = (val: ValueType) => {
    const { speedLimit, speedLimitSize, speedLimitUnit } = formData;
    if (!speedLimit) return { result: true }; // 如果不限速，则 不进行校验
    if (formData.speedLimitSize > 10000) {
      return {
        result: false,
        message: i18n_t('速度不能大于{slot1}', { slot1: `10000${formData.speedLimitUnit}` }),
        type: 'error',
      };
    }

    // kbs 小于 128kb/s
    if (formData.speedLimitUnit === SPEED_UNIT.kbs && formData.speedLimitSize < SPEED_DEFAULT_VALUE.speedKbs) {
      return {
        result: false,
        message: i18n_t(`速度不能小于{slot1}`, { slot1: `${SPEED_DEFAULT_VALUE.speedKbs}${SPEED_UNIT.kbs}` }),
        type: 'error',
      };
    }
    // mbs 小于 0.2mb/s
    if (formData.speedLimitUnit === SPEED_UNIT.mbs && formData.speedLimitSize < SPEED_DEFAULT_VALUE.speedMbs) {
      return {
        result: false,
        message: i18n_t(`速度不能小于{slot1}`, { slot1: `${SPEED_DEFAULT_VALUE.speedMbs}${SPEED_UNIT.mbs}` }),
        type: 'error',
      };
    }
    return { result: true };
  };
  // 验证最小速度范围警告
  const validateMinSpeed = () => {
    const { batchLimit, speedLimit, speedLimitSize, speedLimitUnit, batchAuto } = formData;
    const transferSpeedKbs = SystemSetting.convertTransferSpeed(speedLimitSize, speedLimitUnit);
    // 只有在传输自定义模式才有效
    // 在合法范围内才会进行警告，否则交给其他校验规则来提示错误
    if (batchLimit >= SPEED_DEFAULT_VALUE.minFileLimit && batchLimit <= SPEED_DEFAULT_VALUE.maxFileLimit) {
      if (!speedLimit) return { result: true };
      if (!batchAuto) {
        const transferSpeedKbs = SystemSetting.convertTransferSpeed(speedLimitSize, speedLimitUnit);
        // 合理范围阈值（每一个请求至少130kb/s)
        const normalSpeedRangeBytes = batchLimit * 130 * 1024;
        // 在speedLimitSize/speedLimitUnit 小于等于合理范围内 才开始警告
        if (transferSpeedKbs * 1024 < normalSpeedRangeBytes) {
          const { speed, unit } = getSpeedAndUnit(normalSpeedRangeBytes);
          return {
            result: false,
            message: i18n_t(`当前设置在网速低于{slot1}{slot2}/s时可能会失效`, {
              slot1: speed,
              slot2: unit,
            }),
            type: 'warning',
          };
        }
      }
      if (batchAuto && transferSpeedKbs < 5 * 130) {
        const { speed, unit } = getSpeedAndUnit(5 * 130 * 1024);
        return {
          result: false,
          message: i18n_t(`当前设置在网速低于{slot1}{slot2}/s时可能会失效`, {
            slot1: speed,
            slot2: unit,
          }),
          type: 'warning',
        };
      }
    }
    return { result: true };
  };
  // 验证规则
  const maxFile = type === 'upload' ? SPEED_DEFAULT_VALUE.maxFileLimit : SPEED_DEFAULT_VALUE.downloadMaxFileLimit;
  const formRules = {
    // 文件并发数量
    batchLimit: [
      { required: true, number: true, message: i18n_t('请输入整数'), trigger: 'change' },
      { validator: validateIntegerField, trigger: 'change' },
      {
        trigger: 'change',
        min: SPEED_DEFAULT_VALUE.minFileLimit,
        message: i18n_t(`值不能小于{slot1}`, {
          slot1: `${SPEED_DEFAULT_VALUE.minFileLimit}`,
        }),
        required: true,
      },
      // 下载情况值要小一下
      {
        trigger: 'change',
        max: maxFile,
        message: i18n_t('值不能大于{slot1}', {
          slot1: `${maxFile}`,
        }),
        required: true,
      },
      /* { validator: validateMinSpeed, trigger: 'change' }, */
    ],

    // 限速范围 & 限速单位
    speedLimit: [
      {
        validator: sizeAndUnitValidator,
        trigger: 'change',
      },
    ],
  };

  // 若不限速，则设置为0mb/s
  watch(
    () => formData.speedLimit,
    (value: boolean) => {
      if (!value) {
        formData.speedLimitSize = SPEED_DEFAULT_VALUE.speedKbs;
        formData.speedLimitUnit = SPEED_UNIT.kbs;
      }
    },
    { immediate: true },
  );

  // 若传输模式为最优模式，则重置文件并发数
  watch(
    () => formData.batchAuto,
    (value: boolean) => {
      if (value) {
        formData.batchLimit = SPEED_DEFAULT_VALUE.fileLimit;
      }
    },
    { immediate: true },
  );

  /**
   * 公有传输列表设置
   */
  const transmissionMethods = {
    submitForm: () => {
      const $form = vm.$refs?.form as any;
      $form.submit();
    },
    // 重置为系统默认值
    reset() {
      if (type === 'upload') {
        Object.assign(formData, defaultTransmissionSetting.defaultUploadSetting);
      }
      if (type === 'download') {
        Object.assign(formData, defaultTransmissionSetting.defaultDownloadSetting);
      }
    },
    onSubmit: (submitContext: SubmitContext<RangeLimit>): void => {
      const { validateResult, firstError } = submitContext;
      const canNext = (() => {
        if (validateResult === true) {
          return true;
        }
        const keys = Object.keys(validateResult);
        if (typeof validateResult !== 'boolean' && keys?.length === 1 && keys?.toString() === 'batchLimit') {
          // 若是batchLimit，则忽略警告类型的错误后，若没有其他错误，也可进行提交
          const batchLimitRes = validateResult?.batchLimit as any as Array<AllValidateResult>;
          const goodResult = batchLimitRes.filter(item => {
            return item.type !== 'warning';
          });
          return goodResult?.length === 0;
        }
        return false;
      })();
      if (canNext) {
        storageMethods.setStorage(formData, storeKey.value);
        clearFormDataCache();
        // 主动触发流管理器，进行更新速度
        if (type === 'upload') {
          uploadStreamManager.triggerFixSpeed();
          uploadTaskManager.updateSettingBySystemSetting();
        }
        if (type === 'download') {
          downloadStreamManager.triggerFixSpeed();
          downloadTaskManager.updateSettingBySystemSetting();
        }
        tips.success(i18n_t('保存成功！'));
        toggleMethods.updateTabs();
        //  loganylwu:和幽扬沟通后，认为目前修改并发数量为系统自动处理，则上报变得无意义，于是暂时隐去
        // 保存成功后上报数据
        // {setting_upload_type:1} |  {setting_download_type:1}
        // const code = formType.value === 'up' ? 'setting_upload' : 'setting_download';
        // const key = formType.value === 'up' ? 'setting_upload_type' : 'setting_download_type';
        // const params = { [key]: diffDefault.value ? 1 : 0 };
        // reporter.report(code, params);
      } else {
        firstError && tips.error(firstError);
      }
    },
  };

  // 先进行默认值的赋值，再进行其他操作
  if (type === 'upload') {
    Object.assign(formData, defaultFormData);
  }
  if (type === 'download') {
    Object.assign(formData, defaultFormData);
  }

  const { storageMethods, isEqual, clearFormDataCache } = storageHandler(formData, storeKey.value);

  const { toggleMethods, confirming } = useBeforeLeave();

  // 若batchLimit不是默认值，则修改为自定义模式
  if (formData.batchLimit !== defaultFormData.batchLimit && formData.batchAuto) {
    formData.batchAuto = false;
    storageMethods.setStorage(formData, storeKey.value);
    clearFormDataCache();
  }
  if (formData.batchLimit > SPEED_DEFAULT_VALUE.maxFileLimit) {
    formData.batchLimit = SPEED_DEFAULT_VALUE.maxFileLimit;
    storageMethods.setStorage(formData, storeKey.value);
    clearFormDataCache();
  }
  return {
    formData,
    speedDecimalPlaces,
    confirming,
    isEqual,
    storeKey,
    formRules,
    defaultTransmissionSetting,
    toggleMethods,
    transmissionMethods,
  };
};
